import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, IconButton, Menu, MenuItem, Select, Skeleton, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { ITemplate } from '../../../types/damTypes';
import { getPresentationAsset, listDamTemplates, listPresentationCategories, searchPresentationAssets } from '../api';
import { defaultLibrarySort, LibraryCatalogKind, LibrarySort, resolveCatalogKind } from '../libraryCatalog';
import { LibraryIntent, PresentationAssetDetail as Detail, VisibleAsset } from '../types';
import { COPY } from '../presentationCopy';
import { PRES, TYPE } from '../chrome';
import { AssetThumbnail } from '../ui';
import { focusVisibleSx } from '../ui/focus';
import { resolveMediaUrl } from './slidePreview';
import PresentationAssetDetail from './PresentationAssetDetail';
import {
  CARD_GAP,
  CARD_WIDTH,
  MAX_LIBRARY_WIDTH,
  MIN_LIBRARY_WIDTH,
  columnsForWidth,
  widthForColumns,
} from './assetBrowserLayout';

export const PRESENTATION_ASSET_DRAG = 'application/x-athena-presentation-asset';

interface AssetBrowserProps {
  open: boolean;
  embedded?: boolean;
  panelWidth?: number;
  intent?: LibraryIntent;
  revealAssetId?: string | null;
  revealNonce?: number;
  lockedCategoryName?: string | null;
  onClose: () => void;
  onPlace: (asset: VisibleAsset, intent: LibraryIntent, meta?: { catalogKind?: LibraryCatalogKind | null }) => void;
}

type CategoryItem = {
  _id: string;
  name: string;
  parentCategory?: string | { _id: string; name: string } | null;
};

function parentId(category: CategoryItem): string | null {
  if (!category.parentCategory) return null;
  return typeof category.parentCategory === 'string' ? category.parentCategory : category.parentCategory._id;
}

const LIBRARY_WIDTH_KEY = 'athena.presentations.libraryWidth';
const PAGE_SIZE = 24;

function readStoredWidth(): number {
  try {
    const stored = Number(window.localStorage.getItem(LIBRARY_WIDTH_KEY));
    if (stored >= MIN_LIBRARY_WIDTH && stored <= MAX_LIBRARY_WIDTH) {
      return widthForColumns(columnsForWidth(stored));
    }
  } catch {
    return widthForColumns(2);
  }
  return widthForColumns(2);
}

const AssetBrowser: React.FC<AssetBrowserProps> = ({ open, embedded, panelWidth, intent = 'insert-image', revealAssetId, revealNonce, lockedCategoryName, onClose, onPlace }) => {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assets, setAssets] = useState<VisibleAsset[]>([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [templates, setTemplates] = useState<ITemplate[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [sort, setSort] = useState<LibrarySort>('newest');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [width, setWidth] = useState(readStoredWidth);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [moreEl, setMoreEl] = useState<HTMLElement | null>(null);
  const generationRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    listPresentationCategories().then(setCategories).catch(() => setCategories([]));
    listDamTemplates().then(setTemplates).catch(() => setTemplates([]));
  }, [open]);

  useEffect(() => {
    if (!lockedCategoryName || !categories.length) return;
    const wanted = lockedCategoryName.toLowerCase();
    const match = categories.find((category) => category.name.toLowerCase() === wanted)
      || categories.find((category) => category.name.toLowerCase().includes(wanted));
    if (match) {
      setSelectedCategoryId(match._id);
      setExpandedId(match._id);
    }
  }, [lockedCategoryName, categories]);

  useEffect(() => {
    if (!open || !revealAssetId) return;
    let cancelled = false;
    setError(null);
    void getPresentationAsset(revealAssetId).then((next) => {
      if (!cancelled) setDetail(next);
    }).catch(() => {
      if (!cancelled) {
        setDetail(null);
        setError(COPY.assetsFailed);
      }
    });
    return () => { cancelled = true; };
  }, [open, revealAssetId, revealNonce]);

  const loadPage = async (skip: number, replace: boolean) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const generation = replace ? generationRef.current + 1 : generationRef.current;
    generationRef.current = generation;
    if (replace) {
      setLoading(true);
      setError(null);
    } else {
      setLoadingMore(true);
    }
    try {
      const page = await searchPresentationAssets(query, selectedCategoryId || undefined, { skip, limit: PAGE_SIZE, sort }, controller.signal);
      if (generation !== generationRef.current) return;
      setTotal(page.total);
      setAssets((current) => replace ? page.items : [...current, ...page.items]);
      setError(null);
    } catch (err: any) {
      if (err?.code === 'ERR_CANCELED' || controller.signal.aborted) return;
      if (generation !== generationRef.current) return;
      if (replace) {
        setAssets([]);
        setTotal(0);
      }
      setError(err?.response?.data?.message || COPY.assetsFailed);
    } finally {
      if (generation === generationRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  useEffect(() => {
    if (!open) return;
    setDetail(null);
    const timer = setTimeout(() => { void loadPage(0, true); }, 250);
    return () => clearTimeout(timer);
  }, [open, query, selectedCategoryId, sort]);

  useEffect(() => {
    if (!open || detail) return;
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver((entries) => {
      if (!entries[0]?.isIntersecting || loading || loadingMore || error) return;
      if (assets.length === 0 || assets.length >= total) return;
      void loadPage(assets.length, false);
    }, { rootMargin: '200px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [open, detail, assets.length, total, loading, loadingMore, error]);

  const catalogKind = useMemo(
    () => resolveCatalogKind(selectedCategoryId, categories),
    [selectedCategoryId, categories]
  );
  const catalogKindRef = useRef(catalogKind);
  if (catalogKindRef.current !== catalogKind) {
    catalogKindRef.current = catalogKind;
    setSort(defaultLibrarySort(catalogKind));
  }

  const mains = useMemo(() => {
    const all = categories.filter((category) => !parentId(category));
    const preferred = ['Produkter', 'Vin', 'Logo', 'Miljö', 'Butiksbilder', 'Event'];
    return [...all].sort((left, right) => {
      const a = preferred.findIndex((name) => name.toLowerCase() === left.name.toLowerCase());
      const b = preferred.findIndex((name) => name.toLowerCase() === right.name.toLowerCase());
      if (a === -1 && b === -1) return left.name.localeCompare(right.name, 'sv');
      if (a === -1) return 1;
      if (b === -1) return -1;
      return a - b;
    });
  }, [categories]);
  const visibleMains = mains.slice(0, 6);
  const extraMains = mains.slice(6);
  const subs = useMemo(
    () => expandedId ? categories.filter((category) => parentId(category) === expandedId) : [],
    [categories, expandedId]
  );

  const startResize = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = width;
    const onMove = (moveEvent: PointerEvent) => {
      const next = Math.min(MAX_LIBRARY_WIDTH, Math.max(MIN_LIBRARY_WIDTH, startWidth + (startX - moveEvent.clientX)));
      setWidth(next);
    };
    const onUp = (upEvent: PointerEvent) => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      const raw = Math.min(MAX_LIBRARY_WIDTH, Math.max(MIN_LIBRARY_WIDTH, startWidth + (startX - upEvent.clientX)));
      const next = widthForColumns(columnsForWidth(raw));
      setWidth(next);
      try { window.localStorage.setItem(LIBRARY_WIDTH_KEY, String(next)); } catch { /* ignore */ }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  if (!open) return null;

  const resolvedWidth = embedded ? (panelWidth || width) : width;

  const openDetail = async (asset: VisibleAsset) => {
    try {
      setDetail(await getPresentationAsset(asset._id));
    } catch {
      setDetail({
        ...asset,
        tags: [],
        categories: [],
        versions: [],
        metadata: {},
        placeable: Boolean(asset.previewUrl),
        placeKind: intent === 'insert-product' ? 'product' : 'image',
      });
    }
  };

  return (
    <Box sx={{
      width: embedded ? '100%' : width,
      bgcolor: '#fff',
      borderLeft: embedded ? 'none' : '1px solid #ddd',
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      position: 'relative',
      flexShrink: 0,
      flex: embedded ? 1 : undefined,
    }}>
      {!embedded && (
        <Box
          onPointerDown={startResize}
          title="Dra för att ändra bredd"
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 8,
            cursor: 'ew-resize',
            zIndex: 2,
            '&:hover': { bgcolor: 'rgba(196,163,90,0.35)' },
          }}
        />
      )}
      {detail ? (
        <PresentationAssetDetail
          asset={detail}
          categories={categories}
          templates={templates}
          intent={intent}
          onClose={() => setDetail(null)}
          onPlace={(asset, placeIntent) => onPlace(asset, placeIntent, { catalogKind })}
        />
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 1 }}>
            <Typography sx={{ color: PRES.textPrimary, flex: 1, fontFamily: TYPE.ui, ...TYPE.title }}>{COPY.library}</Typography>
            {!embedded && <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>}
          </Box>
          <Box sx={{ px: 1.5, pb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            <Box
              component="button"
              type="button"
              onClick={() => { setSelectedCategoryId(null); setExpandedId(null); }}
              sx={{
                px: 1, py: 0.4, borderRadius: 1, cursor: 'pointer', fontFamily: TYPE.ui, ...TYPE.caption, border: 0,
                bgcolor: !selectedCategoryId ? PRES.interactive : PRES.surfaceSunken,
                color: !selectedCategoryId ? PRES.white : PRES.textPrimary,
                ...focusVisibleSx,
              }}
            >
              {COPY.allCategories}
            </Box>
            {visibleMains.map((category) => (
              <Box
                key={category._id}
                component="button"
                type="button"
                onClick={() => {
                  setSelectedCategoryId(category._id);
                  setExpandedId(category._id);
                }}
                sx={{
                  px: 1, py: 0.4, borderRadius: 1, cursor: 'pointer', fontFamily: TYPE.ui, ...TYPE.caption, border: 0,
                  bgcolor: selectedCategoryId === category._id ? PRES.interactive : PRES.surfaceSunken,
                  color: selectedCategoryId === category._id ? PRES.white : PRES.textPrimary,
                  ...focusVisibleSx,
                }}
              >
                {category.name}
              </Box>
            ))}
            {!!extraMains.length && (
              <>
                <Box
                  component="button"
                  type="button"
                  onClick={(event) => setMoreEl(event.currentTarget)}
                  sx={{ px: 1, py: 0.4, borderRadius: 1, border: `1px solid ${PRES.borderSubtle}`, bgcolor: PRES.surface, color: PRES.textPrimary, cursor: 'pointer', fontFamily: TYPE.ui, ...TYPE.caption, ...focusVisibleSx }}
                >
                  {COPY.moreCategories}
                </Box>
                <Menu anchorEl={moreEl} open={Boolean(moreEl)} onClose={() => setMoreEl(null)}>
                  {extraMains.map((category) => (
                    <MenuItem
                      key={category._id}
                      selected={selectedCategoryId === category._id}
                      onClick={() => {
                        setSelectedCategoryId(category._id);
                        setExpandedId(category._id);
                        setMoreEl(null);
                      }}
                    >
                      {category.name}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}
          </Box>
          {!!subs.length && (
            <Box sx={{ px: 1.5, pb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
              {subs.map((category) => (
                <Box
                  key={category._id}
                  component="button"
                  type="button"
                  onClick={() => setSelectedCategoryId(category._id)}
                  sx={{
                    px: 0.7, py: 0.15, borderRadius: 0.75, cursor: 'pointer', fontFamily: TYPE.ui, ...TYPE.caption,
                    border: selectedCategoryId === category._id ? `1px solid ${PRES.interactive}` : `1px solid ${PRES.borderSubtle}`,
                    bgcolor: selectedCategoryId === category._id ? PRES.surfaceSunken : PRES.surface,
                    color: PRES.textPrimary,
                    ...focusVisibleSx,
                  }}
                >
                  {category.name}
                </Box>
              ))}
            </Box>
          )}
          <TextField
            size="small"
            placeholder={COPY.searchAssets}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            sx={{ mx: 1.5, mb: 1 }}
          />
          <Box sx={{ px: 1.5, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.caption, color: PRES.textTertiary }}>
              {COPY.shownOfTotal} {assets.length} {COPY.ofTotal} {total}
            </Typography>
            <Select
              size="small"
              aria-label={COPY.sortLabel}
              value={sort}
              onChange={(event) => setSort(event.target.value as LibrarySort)}
              sx={{ fontSize: 12, maxWidth: 160, height: 32 }}
            >
              {(catalogKind === 'product' || catalogKind === 'wine') && (
                <MenuItem value="article">{COPY.sortArticle}</MenuItem>
              )}
              <MenuItem value="alpha">{COPY.sortAlpha}</MenuItem>
              <MenuItem value="newest">{COPY.sortNewest}</MenuItem>
            </Select>
          </Box>
          <Box sx={{ flex: 1, overflowX: 'hidden', overflowY: 'auto', px: 1.5, pb: 2 }}>
            {error && (
              <Alert
                severity="error"
                sx={{ mb: 1 }}
                action={<Button color="inherit" size="small" onClick={() => void loadPage(0, true)}>{COPY.retry}</Button>}
              >
                {error}
              </Alert>
            )}
            {loading ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${columnsForWidth(resolvedWidth)}, ${CARD_WIDTH}px)`, gap: `${CARD_GAP}px` }}>
                {Array.from({ length: 8 }).map((_, index) => (
                  <Skeleton key={index} variant="rectangular" width={CARD_WIDTH} height={120} />
                ))}
              </Box>
            ) : (
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${columnsForWidth(resolvedWidth)}, ${CARD_WIDTH}px)`,
                gap: `${CARD_GAP}px`,
                justifyContent: 'start',
              }}>
                {assets.map((asset) => (
                  <Box
                    key={asset._id}
                    draggable
                    onDragStart={(event) => {
                      event.dataTransfer.setData(PRESENTATION_ASSET_DRAG, JSON.stringify(asset));
                      event.dataTransfer.effectAllowed = 'copy';
                    }}
                    onClick={() => { void openDetail(asset); }}
                    onDoubleClick={() => { if (asset.previewUrl) onPlace(asset, intent, { catalogKind }); }}
                    sx={{
                      width: CARD_WIDTH,
                      border: `1px solid ${PRES.borderSubtle}`,
                      borderRadius: 1,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      bgcolor: PRES.surface,
                      position: 'relative',
                      '&:hover .asset-place, &:focus-within .asset-place': { opacity: 1 },
                      '&:hover': { borderColor: PRES.interactive },
                    }}
                  >
                    <Box sx={{ position: 'relative' }}>
                      <AssetThumbnail
                        src={asset.previewUrl ? resolveMediaUrl(asset.previewUrl) : undefined}
                        alt=""
                        fit="contain"
                      />
                      <IconButton
                        className="asset-place"
                        size="small"
                        disabled={!asset.previewUrl}
                        title={COPY.place}
                        aria-label={COPY.place}
                        onClick={(event) => {
                          event.stopPropagation();
                          onPlace(asset, intent, { catalogKind });
                        }}
                        onPointerDown={(event) => event.stopPropagation()}
                        sx={{
                          position: 'absolute',
                          top: 6,
                          right: 6,
                          opacity: 0,
                          bgcolor: PRES.interactive,
                          color: PRES.white,
                          width: 28,
                          height: 28,
                          '&:hover': { bgcolor: PRES.interactiveHover },
                        }}
                      >
                        <AddIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      {catalogKind === 'product' && (
                        <IconButton
                          className="asset-place"
                          size="small"
                          disabled={!asset.previewUrl}
                          title="Bild med produktinformation"
                          aria-label="Bild med produktinformation"
                          onClick={(event) => {
                            event.stopPropagation();
                            onPlace(asset, 'insert-product', { catalogKind });
                          }}
                          onPointerDown={(event) => event.stopPropagation()}
                          sx={{
                            position: 'absolute',
                            top: 40,
                            right: 6,
                            opacity: 0,
                            bgcolor: '#4DCCA6',
                            color: PRES.navy,
                            width: 28,
                            height: 28,
                            fontFamily: TYPE.ui,
                            fontSize: 13,
                            fontWeight: 750,
                            '&:hover': { bgcolor: '#3BB890' },
                          }}
                        >
                          P
                        </IconButton>
                      )}
                    </Box>
                    <Typography sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      minHeight: 32,
                      px: 0.75,
                      pt: 0.75,
                      textAlign: 'center',
                      fontFamily: TYPE.ui,
                      ...TYPE.caption,
                      fontWeight: 650,
                      color: PRES.textPrimary,
                    }}>
                      {asset.title || asset.fileName}
                    </Typography>
                    <Typography noWrap sx={{ display: 'block', px: 0.75, pb: 0.75, minHeight: 18, textAlign: 'center', fontFamily: TYPE.ui, ...TYPE.caption, color: PRES.textTertiary }}>
                      {asset.metaLine || ' '}
                    </Typography>
                  </Box>
                ))}
                {!assets.length && !error && (
                  <Typography variant="body2" color="text.secondary" sx={{ gridColumn: '1 / -1' }}>
                    {COPY.noAssets}
                  </Typography>
                )}
                <Box ref={sentinelRef} sx={{ gridColumn: '1 / -1', height: 1 }} />
                {loadingMore && <CircularProgress size={18} sx={{ gridColumn: '1 / -1', mx: 'auto' }} />}
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default AssetBrowser;
